/**
 * WebFavoritesPageViewModel - Web Platform Adapter
 *
 * Web-specific adapter for FavoritesPageViewModel.
 * Wires the shared VM with web platform services, Zustand stores,
 * and the openFavoriteProductSheet action from useUIStore.
 */

import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  createFavoritesPageViewModel,
  mapFavoritesToProducts,
  createWebPlatformServices,
  WebFavoritesPageService,
} from '@restaurant/viewmodels'
import { useAuthStore, useRestaurantStore, useUIStore } from '@restaurant/core'

/**
 * Web adapter for FavoritesPageViewModel.
 * Injects web platform services, auth / restaurant state, and UI-store callbacks.
 */
export const useWebFavoritesPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Zustand selectors
  const user = useAuthStore((s) => s.user)
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant)
  const setFavoritesCount = useUIStore((s) => s.setFavoritesCount)
  const openFavoriteProductSheet = useUIStore((s) => s.openFavoriteProductSheet)
  const favoritesCountVersion = useUIStore((s) => s.favoritesCountVersion)

  // Memoised platform services
  const platformServices = useMemo(
    () => createWebPlatformServices(navigate, location),
    [navigate, location],
  )

  // Memoised favorites-page service (stable reference)
  const favoritesPageService = useMemo(() => new WebFavoritesPageService(), [])

  // Create and invoke the shared hook
  const useFavoritesPage = useMemo(
    () =>
      createFavoritesPageViewModel(platformServices, {
        favoritesPageService,
        getUserId: () => user?.id ?? null,
        getRestaurantStoreId: () => currentRestaurant?.id ?? null,
        onFavoritesCountChange: (count: number) => setFavoritesCount(count),
      }),
    [platformServices, favoritesPageService, user?.id, currentRestaurant?.id, setFavoritesCount],
  )

  const vm = useFavoritesPage()

  // Re-fetch favorites when favoritesCountVersion changes (triggered by update/unfavorite)
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    vm.refreshFavorites()
  }, [favoritesCountVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Override getPageContentFavoritesProps to wire onProductPress → openFavoriteProductSheet
  const getPageContentFavoritesProps = useCallback(() => {
    const baseProps = vm.getPageContentFavoritesProps()

    const products = mapFavoritesToProducts(vm.favorites, (favoriteId, productId) => {
      const fav = vm.favorites.find((f) => f.id === favoriteId)
      openFavoriteProductSheet({
        favoriteId,
        productId,
        productName: fav?.productName || '',
        productImageUrl: fav?.productImageUrl,
        rawSelectedOptions: fav?.rawSelectedOptions || [],
        selectedPriceVariant: fav?.selectedPriceVariant || 'base',
      })
    })

    return {
      ...baseProps,
      products,
    }
  }, [vm, openFavoriteProductSheet])

  return {
    ...vm,
    getPageContentFavoritesProps,
  }
}
